import { useContext, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Pagination, Typography } from "@mui/material";
import { backend, CategoryFindingSummary, LensHit, makeLensHitFilters, PageType } from "Backend";
import Flex from "components/Flex";
import PCheckbox from "components/PCheckbox";
import MedicationHitsTable from "./MedicationHitsTable";
import { patternPeri } from "theme/palette";
import { StateHandle } from "StateHandle";
import { usePaginationState } from "helpers/Pagination";
import { LensHitFilterState } from "../LensHitFilterState";
import { FormField, LensHitsFilterParamsInput } from "generated/graphql";
import { CaseFormContext } from "contexts/CaseFormContext";
import { styled } from "@mui/system";
import { useSnackbar } from "notistack";

const DialogContainer = styled(Dialog)({});

const DialogPaper = styled("div")(() => ({
  minHeight: "80vh",
  maxHeight: "80vh",
}));

export interface PinHitsTableDialogProps {
  lensId: number;
  caseId: number;
  selectedCategory: CategoryFindingSummary;
  openHandle: StateHandle<boolean>;
  onPin: () => unknown;
  suggestedField: FormField;
  lensHitFilterState: LensHitFilterState;
}

const PAGE_SIZE = 30;

const PinHitsTableDialog = (props: PinHitsTableDialogProps) => {
  const { lensId, caseId, selectedCategory, openHandle, lensHitFilterState } = props;
  const caseForm = useContext(CaseFormContext);
  const { enqueueSnackbar } = useSnackbar();

  const closeDialog = () => openHandle[1](false);

  const [hits, setHits] = useState<LensHit[]>([]);
  const [hitsFullCount, setHitsFullCount] = useState(0);
  const { pageIndex, setPageIndex, pageCount, pageNumber, startOffsetForPage } = usePaginationState(
    hitsFullCount,
    PAGE_SIZE,
    0
  );
  const offsetStart = startOffsetForPage(pageIndex);
  const [pharmacyOnly, setPharmacyOnly] = useState<boolean>(true);

  const generateFilters = (): LensHitsFilterParamsInput => {
    const filters: Partial<LensHitsFilterParamsInput> = {
      ...lensHitFilterState.currentFilters,
      rules: [selectedCategory.ruleType],
      // We never want the "collapse repetitive findings" filter to be 'on' for this call
      filterIdentity: false,
      pageTypes: pharmacyOnly ? [PageType.Pharmacy] : lensHitFilterState.currentFilters.pageTypes,
    };

    return makeLensHitFilters(filters);
  };

  const pinExpandedFindingGroupToProfile = () => {
    backend
      .addLensHitsToFormField({
        lensId,
        caseId,
        fieldId: props.suggestedField.id,
        filters: generateFilters(),
      })
      .then((result) => {
        backend.mapSuccess(result, () => {
          void caseForm.reloadServerValues();
          props.onPin();
        });
      })
      .catch((error) => {
        console.error("Error adding lens hits to form field", error);
        enqueueSnackbar("Error adding lens hits to form field", { variant: "error" });
      });
  };

  useEffect(() => {
    backend
      .getLensRun({
        lensId,
        caseId,
        offset: offsetStart,
        limit: PAGE_SIZE,
        includeSurroundings: false,
        filters: generateFilters(),
      })
      .then((response) => {
        setHits(response.hits);
        setHitsFullCount(response.fullCount);
      })
      .catch((error) => {
        console.error("Error getting lens hits", error);
      });
  }, [lensId, caseId, selectedCategory.ruleType, pageIndex, pharmacyOnly]);

  return (
    <DialogContainer fullWidth maxWidth="xl" open={openHandle[0]} onClose={closeDialog}>
      <DialogPaper>
        <DialogContent>
          <DialogTitle>
            {selectedCategory.ruleType.subtype} - {selectedCategory.ruleType.description} Findings
          </DialogTitle>
          <Flex
            p={1}
            alignItems="flex-start"
            style={{ borderStyle: "solid", borderRadius: "2px", borderWidth: "1px", borderColor: patternPeri.A400 }}>
            <Typography>Pin All Findings?</Typography>
            <Typography>
              This action will add <b>{hitsFullCount}</b>{" "}
              <b>
                {selectedCategory.ruleType.subtype} - {selectedCategory.ruleType.description}
              </b>{" "}
              hits to the profile field <b>{props.suggestedField.name}</b>
            </Typography>
            <Button
              color="default"
              onClick={() => {
                pinExpandedFindingGroupToProfile();
                closeDialog();
              }}>
              Pin Findings
            </Button>
          </Flex>
          <PCheckbox
            label="Prescription Findings Only"
            checked={pharmacyOnly}
            onChange={(_evt, checked) => (checked ? setPharmacyOnly(true) : setPharmacyOnly(false))}
          />
          <MedicationHitsTable lensHits={hits.map((hit, idx) => [idx + offsetStart, hit])} />
          <Pagination page={pageNumber} onChange={(_evt, page) => setPageIndex(page - 1)} count={pageCount} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </DialogPaper>
    </DialogContainer>
  );
};

export default PinHitsTableDialog;
